import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import UserService from '@/services/user.service';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import Loader from '@/shared-components/loader/Loader';
import Utils from '@/utils';
import { resolveUserProfileTypeWithFallback } from '@/utils/userProfileImage';

import '@/pages/profile/channels/styles/ChannelMenu.css'; // Reuse the same CSS

export default function DirectMessagesMenu() {
  const { state: globalState, stateHandler } = useStateHandler();
  const t = useTranslations('common.profile.channels');
  const profileName = globalState.user_session?.user_username;
  const location = useLocation();
  const [dmList, setDmList] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const stateHandlerRef = useRef(stateHandler);

  useEffect(() => {
    stateHandlerRef.current = stateHandler;
  }, [stateHandler]);

  const setChannelUnreadUpdated = useCallback((value) => {
    const handler = stateHandlerRef.current;
    if (!handler) return;
    handler.set('channelUnreadUpdated', value);
  }, []);

  // Persist entry origin for stable back navigation
  useEffect(() => {
    if (location?.state?.fromPath) {
      sessionStorage.setItem('channels_from_path', location.state.fromPath);
    }
  }, [location?.state?.fromPath]);

  const resolveFromPath = () => (
    location?.state?.fromPath
    || sessionStorage.getItem('channels_from_path')
    || '/profile'
  );

  // Function to refresh direct messages.
  function refreshDMs() {
    // Clear old data.
    setLoading(true);
    setDmList([]);
    UserService.getUserChats()
      .then(({ response, data }) => {
        if (response.ok) {
          const list = Array.isArray(data?.data) ? data.data : [];
          setDmList(list);
          if (list.length === 0) {
            setLoading(false);
          }
          setError(null);
        } else {
          setError("Failed to load direct messages.");
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("Error fetching direct messages:", err);
        setError("Failed to load direct messages.");
        setLoading(false);
      });
  }

  useEffect(() => {
    refreshDMs();
  }, []);

  // Enrich each DM with owner profile information.
  useEffect(() => {
    if (dmList.length === 0) {
      setLoading(false);
      return;
    }
    setLoading(false);
  }, [dmList]);

  // Refresh DMs when the window regains focus.
  useEffect(() => {
    function handleFocus() {
      refreshDMs();
    }
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  // Listen for global unread update.
  useEffect(() => {
    if (globalState.channelUnreadUpdated) {
      refreshDMs();
      setChannelUnreadUpdated(false);
    }
  }, [globalState.channelUnreadUpdated, setChannelUnreadUpdated]);
  if (error && !loading) {
    return (
      <div className="channel-menu__container">
        <div className="channel-menu__error">{error}</div>
      </div>
    );
  }

  const list = dmList;

  return (
    <div className="channel-menu__container">
      <div className="channel-menu__header">
        <BackArrowIcon
          className="channel-menu__go-back"
          onClick={() => {
            const from = resolveFromPath();
            if (from) {
              sessionStorage.removeItem('channels_from_path');
              navigate(from, { replace: true });
            } else {
              navigate(-1);
            }
          }}
        />
        <h1 className="channel-menu__title">{profileName}</h1>
      </div>

      {/* Updated Toggle for Channels/Chats */}
      <div className="user-profile__community-navigation element-non-selectable">
        <div 
          className="user-profile__community-navigation-link"
          onClick={() => {
            const fromPath = resolveFromPath();
            if (fromPath) {
              sessionStorage.setItem('channels_from_path', fromPath);
            }
            navigate("/channels", { state: { fromPath } });
          }}        
        >
          <span className="user-profile__community-navigation-link-text">{t('navigation.channels')}</span>
        </div>
        <div 
          className="user-profile__community-navigation-link active"
          onClick={() => {
            const fromPath = resolveFromPath();
            if (fromPath) {
              sessionStorage.setItem('channels_from_path', fromPath);
            }
            navigate("/chat/dm", { state: { fromPath } });
          }}
        >
          <span className="user-profile__community-navigation-link-text">{t('navigation.chats')}</span>
        </div>
      </div>

      <div className="channel-menu__list">
        {loading && (
          <div className="channel-menu__loader">
            <Loader inline thin />
          </div>
        )}
        {list.map((dm) => {
          const latestMessage = dm.latestMessage || null;
          const channelId = dm.channelId || "unknown";
          const senderUsername = latestMessage?.message_sender?.user_username || '';
          return (
            <Link
              to={`/chat/dm/${channelId}`}
              key={`dm-${channelId}`}
              className="channel-menu__item"
            >
              <div className="channel-menu__item-avatar">
                <Link
                  className="no-underline"
                  to={`/user/${channelId}`}
                  state={{ from: true }}
                >
                  <UserProfileImage
                    image={dm.ownerImage || null}
                    typeOfProfile={resolveUserProfileTypeWithFallback(
                      'collector',
                      dm.ownerType,
                      latestMessage?.message_sender?.user_type,
                      latestMessage?.message_sender?.__t,
                      latestMessage?.message_sender?.type,
                    )}
                    className="chat-message__user-image"
                  />
                </Link>
              </div>
              <div className="channel-menu__item-info">
                <div className="channel-menu__item-header">
                  <span className="channel-menu__item-name">
                    {dm.ownerName || "Unknown"}
                  </span>
                  <span className="channel-menu__item-timestamp">
                    {latestMessage?.createdAt
                      ? Utils.getTimeOfMessageFromTimestamp(new Date(latestMessage.createdAt).getTime())
                      : ''}
                  </span>
                </div>
                <div className="channel-menu__item-subinfo">
                  <div className="channel-menu__item-message">
                    {senderUsername ? (
                      <>
                        <span className="channel-menu__item-sender">
                          {senderUsername}:
                        </span>{" "}
                      </>
                    ) : null}
                    {latestMessage?.message_sticker ? 'Sticker' : (latestMessage?.message_text || '')}
                  </div>
                  {dm.unreadCount && dm.unreadCount > 0 && (
                    <span className="channel-menu__item-unread">
                      {dm.unreadCount}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
